export interface RoofAtlas {
  level: number; x0: number; y0: number; cols: number; rows: number;
}
export function tilePoint(lon: number, lat: number, level: number): [number, number] {
  const n = 2 ** level;
  return [(lon + 180) / 360 * n, (1 - Math.asinh(Math.tan(lat * Math.PI / 180)) / Math.PI) / 2 * n];
}
/** Bound requests and GPU allocation even when the catalogue spans a wide campus. */
export function roofAtlas(coordinates: number[][], maxTiles = 64, maxLevel = 21, tileSize = 256): RoofAtlas | null {
  if (!coordinates.length || coordinates.some(p => !Number.isFinite(p[0] + p[1]) || Math.abs(p[1]) > 85)) return null;
  for (let level = Math.min(21, maxLevel); level >= 8; level--) {
    const points = coordinates.map(p => tilePoint(p[0], p[1], level));
    const x0 = Math.floor(Math.min(...points.map(p => p[0]))), y0 = Math.floor(Math.min(...points.map(p => p[1])));
    const cols = Math.floor(Math.max(...points.map(p => p[0]))) - x0 + 1, rows = Math.floor(Math.max(...points.map(p => p[1]))) - y0 + 1;
    if (cols * rows <= maxTiles && Math.max(cols, rows) * tileSize <= 4096) return { level, x0, y0, cols, rows };
  }
  return null;
}
/** Prefer a roof containing the supplied point, otherwise the nearest footprint. */
export function focusRoof<T extends {coordinates: number[][]}>(features: T[], origin: {lat: number; lon: number}): T | undefined {
  let closest: T | undefined, distance = Infinity;
  for (const feature of features) {
    const ring = feature.coordinates;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i], [xj, yj] = ring[j];
      if ((yi > origin.lat) !== (yj > origin.lat) && origin.lon < (xj - xi) * (origin.lat - yi) / (yj - yi) + xi) inside = !inside;
    }
    if (inside) return feature;
    for (const [lon, lat] of ring) {
      const d = ((lon - origin.lon) * Math.cos(origin.lat * Math.PI / 180)) ** 2 + (lat - origin.lat) ** 2;
      if (d < distance) { distance = d; closest = feature; }
    }
  }
  return closest;
}
/** Inverse of the campus tangent approximation used by sitePoint. */
export function roofUv(x: number, z: number, origin: {lat: number; lon: number}, atlas: RoofAtlas): [number, number] {
  const phi = origin.lat * Math.PI / 180, e2 = 6.69437999014e-3, d = 1 - e2 * Math.sin(phi) ** 2;
  const lon = origin.lon + x * Math.sqrt(d) / (6378137 * Math.cos(phi)) * 180 / Math.PI;
  const lat = origin.lat - z * d ** 1.5 / (6378137 * (1 - e2)) * 180 / Math.PI;
  const [tx, ty] = tilePoint(lon, lat, atlas.level);
  return [(tx - atlas.x0) / atlas.cols, 1 - (ty - atlas.y0) / atlas.rows];
}
